// ===========================================
// Project Table - Compact List View with Sorting
// ===========================================
// Displays projects in a table format with sortable columns
// and inline filtering capabilities

import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronUp,
  ChevronDown,
  FolderKanban,
  Clock,
  TrendingUp,
  Github,
  Globe
} from 'lucide-react';
import './ProjectTable.scss';

// ===========================================
// Column Definitions
// ===========================================
const TABLE_COLUMNS = [
  { key: 'name', label: 'Project', sortable: true, width: 'flex' },
  { key: 'status', label: 'Status', sortable: true, width: '100px' },
  { key: 'techStack', label: 'Stack', sortable: false, width: '160px' },
  { key: 'totalMonthlyCost', label: 'Cost', sortable: true, width: '80px' },
  { key: 'arrLevel', label: 'ARR', sortable: true, width: '80px' },
  { key: 'lastFocused', label: 'Active', sortable: true, width: '90px' },
  { key: 'links', label: '', sortable: false, width: '60px' }
];

// Status badge configuration
const statusConfig = {
  idea: { color: 'var(--color-status-idea)', label: 'Idea' },
  active: { color: 'var(--color-status-active)', label: 'Active' },
  paused: { color: 'var(--color-status-paused)', label: 'Paused' },
  completed: { color: 'var(--color-status-completed)', label: 'Completed' },
  archived: { color: 'var(--color-status-archived)', label: 'Archived' }
};

// ARR sort order for proper sorting
const arrSortOrder = {
  '$0': 0,
  '$10k': 1,
  '$100k': 2,
  '$1m': 3,
  '$10m': 4,
  '$100m': 5,
  '$1B+': 6
};

const ProjectTable = ({ projects }) => {
  const navigate = useNavigate();
  
  // Sorting state
  const [sortColumn, setSortColumn] = useState('lastFocused');
  const [sortDirection, setSortDirection] = useState('desc');

  // ===========================================
  // Sort Handler
  // ===========================================
  const handleSort = (columnKey) => {
    if (sortColumn === columnKey) {
      // Toggle direction if same column
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column with default descending
      setSortColumn(columnKey);
      setSortDirection('desc');
    }
  };

  // ===========================================
  // Sorted Projects
  // ===========================================
  const sortedProjects = useMemo(() => {
    return [...projects].sort((a, b) => {
      let aVal, bVal;
      
      switch (sortColumn) {
        case 'name':
          // Alphabetical sort
          aVal = (a.name || '').toLowerCase();
          bVal = (b.name || '').toLowerCase();
          break;
          
        case 'status':
          // Sort by status in SDLC order
          const statusOrder = ['idea', 'active', 'paused', 'completed', 'archived'];
          aVal = statusOrder.indexOf(a.status);
          bVal = statusOrder.indexOf(b.status);
          break;
          
        case 'totalMonthlyCost':
          // Sort by total investment (infra + AI)
          aVal = (a.totalMonthlyCost || 0) + (a.aiDevTokenSpend || 0);
          bVal = (b.totalMonthlyCost || 0) + (b.aiDevTokenSpend || 0);
          break;
          
        case 'arrLevel':
          // Sort by ARR tier
          aVal = arrSortOrder[a.arrLevel] || 0;
          bVal = arrSortOrder[b.arrLevel] || 0;
          break;
          
        case 'lastFocused':
          // Sort by date
          aVal = new Date(a.lastFocused || 0);
          bVal = new Date(b.lastFocused || 0);
          break;
          
        default:
          aVal = a[sortColumn];
          bVal = b[sortColumn];
      }
      
      // Handle comparison
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [projects, sortColumn, sortDirection]);

  // ===========================================
  // Format Helpers
  // ===========================================
  const getRelativeTime = (date) => {
    if (!date) return '—';
    
    const now = new Date();
    const past = new Date(date);
    const diffMs = now - past;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Now';
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays < 30) return `${diffDays}d`;
    return past.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatCost = (project) => {
    const total = (project.totalMonthlyCost || 0) + (project.aiDevTokenSpend || 0);
    if (total === 0) return '—';
    return `$${total.toFixed(0)}`;
  };

  // Navigate to project detail
  const handleRowClick = (project) => {
    navigate(`/dashboard/projects/${project._id || project.id}`);
  };

  // ===========================================
  // Render
  // ===========================================
  return (
    <div className="project-table">
      {/* Table Header */}
      <div className="project-table__header">
        {TABLE_COLUMNS.map(column => (
          <div
            key={column.key}
            className={`project-table__th ${column.sortable ? 'project-table__th--sortable' : ''} ${sortColumn === column.key ? 'project-table__th--active' : ''}`}
            style={{ width: column.width, flex: column.width === 'flex' ? 1 : 'none' }}
            onClick={() => column.sortable && handleSort(column.key)}
          >
            <span>{column.label}</span>
            {column.sortable && (
              <span className="project-table__sort-icon">
                {sortColumn === column.key ? (
                  sortDirection === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />
                ) : (
                  <ChevronDown size={12} />
                )}
              </span>
            )}
          </div>
        ))}
      </div>

      {/* Table Body */}
      <div className="project-table__body">
        {sortedProjects.length > 0 ? (
          sortedProjects.map(project => {
            const status = statusConfig[project.status] || statusConfig.idea;
            
            return (
              <div
                key={project._id || project.id}
                className="project-table__row"
                onClick={() => handleRowClick(project)}
              >
                {/* Project Name */}
                <div className="project-table__td project-table__td--name" style={{ flex: 1 }}>
                  <div className="project-table__project-info">
                    <div className="project-table__icon">
                      <FolderKanban size={14} />
                    </div>
                    <div>
                      <span className="project-table__name">{project.name}</span>
                      {project.description && (
                        <span className="project-table__description">
                          {project.description.length > 50 
                            ? `${project.description.substring(0, 50)}...` 
                            : project.description}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Status */}
                <div className="project-table__td" style={{ width: '100px' }}>
                  <span 
                    className="project-table__status-badge"
                    style={{ 
                      background: `${status.color}15`,
                      color: status.color,
                      borderColor: `${status.color}30`
                    }}
                  >
                    {status.label}
                  </span>
                </div>

                {/* Tech Stack */}
                <div className="project-table__td project-table__td--stack" style={{ width: '160px' }}>
                  {project.techStack && project.techStack.length > 0 ? (
                    <div className="project-table__tech">
                      {project.techStack.slice(0, 2).map((tech, i) => (
                        <span key={i} className="project-table__tech-tag">{tech}</span>
                      ))}
                      {project.techStack.length > 2 && (
                        <span className="project-table__tech-more">+{project.techStack.length - 2}</span>
                      )}
                    </div>
                  ) : (
                    <span className="project-table__empty">—</span>
                  )}
                </div>

                {/* Cost */}
                <div className="project-table__td project-table__td--cost" style={{ width: '80px' }}>
                  <span className={formatCost(project) !== '—' ? 'has-value' : ''}>
                    {formatCost(project)}
                  </span>
                </div>

                {/* ARR */}
                <div className="project-table__td project-table__td--arr" style={{ width: '80px' }}>
                  {project.arrLevel && project.arrLevel !== '$0' ? (
                    <span className="project-table__arr">
                      <TrendingUp size={12} />
                      {project.arrLevel}
                    </span>
                  ) : (
                    <span className="project-table__empty">—</span>
                  )}
                </div>

                {/* Last Focused */}
                <div className="project-table__td project-table__td--time" style={{ width: '90px' }}>
                  <span>
                    <Clock size={12} />
                    {getRelativeTime(project.lastFocused)}
                  </span>
                </div>

                {/* Links */}
                <div className="project-table__td project-table__td--links" style={{ width: '60px' }}>
                  {project.repoUrl && (
                    <a
                      href={project.repoUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="project-table__link"
                      title="Repository"
                    >
                      <Github size={14} />
                    </a>
                  )}
                  {project.liveUrl && (
                    <a
                      href={project.liveUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="project-table__link"
                      title="Live Site"
                    >
                      <Globe size={14} />
                    </a>
                  )}
                </div>
              </div>
            );
          })
        ) : (
          <div className="project-table__empty-state">
            <FolderKanban size={32} />
            <p>No projects found</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectTable;

